import React, { useCallback, useState } from 'react';
import { Calculator, Save, RefreshCw, Download, Globe, DollarSign } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import VisaSelectionSection from './VisaSelectionSection';
import ApplicantDetailsSection from './ApplicantDetailsSection';
import AdditionalServicesSection from './AdditionalServicesSection';
import InsuranceSection from './InsuranceSection';
import DocumentSection from './DocumentSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета визового сбора
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  feeBreakdown,
  setFeeBreakdown,
  countries,
  countryCategories,
  visaTypes,
  processingTimes,
  vacServiceFees,
  exchangeRates,
  saveCalculation,
  generateFeeBreakdown
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveName, setSaveName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обработка зависимостей между полями
      if (name === 'country') {
        // Сброс типа визы при смене страны для некоторых специфических стран
        if (value === 'turkey' || value === 'egypt') {
          newState.visaType = 'tourist';
        } else if (value === 'usa') {
          // Для США другие категории виз
          if (['transit', 'medical', 'cultural'].includes(prev.visaType)) {
            newState.visaType = 'tourist';
          }
        }

        // Сбросим опцию VIP для стран, где нет такой возможности
        if (['turkey', 'egypt', 'thailand', 'vietnam', 'india'].includes(value)) {
          if (prev.processingTime === 'vip') {
            newState.processingTime = 'express';
          }
        }
      }

      // Сбросить связанные поля если функция отключена
      if (name === 'includeInsurance' && !checked) {
        newState.insuredDays = '30';
        newState.insuranceCoverage = 'standard';
      }
      
      if (name === 'includeTranslation' && !checked) {
        newState.documentCount = '5';
      }
      
      if (name === 'includeDelivery' && !checked) {
        newState.deliveryType = 'standard';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      country, visaType, visaDuration, applicantsCount, 
      includeInsurance, insuredDays, 
      includeTranslation, documentCount,
      includeDelivery, deliveryType
    } = formData;

    // Проверка основных полей
    if (!country) newErrors.country = 'Пожалуйста, выберите страну.';
    if (!visaType) newErrors.visaType = 'Пожалуйста, выберите тип визы.';
    
    // Проверка длительности визы
    const numVisaDuration = parseIntOrDefault(visaDuration, -1);
    if (numVisaDuration <= 0) newErrors.visaDuration = 'Длительность должна быть положительным числом.';
    if (numVisaDuration > 365) newErrors.visaDuration = 'Максимальная длительность - 365 дней.';
    
    // Проверка количества заявителей
    const numApplicantsCount = parseIntOrDefault(applicantsCount, -1);
    if (!isInteger(applicantsCount) || numApplicantsCount <= 0) {
      newErrors.applicantsCount = 'Введите положительное целое число.';
    }
    if (numApplicantsCount > 10) {
      newErrors.applicantsCount = 'Максимум 10 заявителей для одного расчета.';
    }

    // Проверка страховки, если опция включена
    if (includeInsurance) {
      const numInsuredDays = parseIntOrDefault(insuredDays, -1);
      if (numInsuredDays <= 0) newErrors.insuredDays = 'Количество дней должно быть положительным числом.';
      if (numInsuredDays > 365) newErrors.insuredDays = 'Максимальный срок страховки - 365 дней.';
    }

    // Проверка перевода документов, если опция включена
    if (includeTranslation) {
      const numDocumentCount = parseIntOrDefault(documentCount, -1);
      if (!isInteger(documentCount) || numDocumentCount <= 0) {
        newErrors.documentCount = 'Введите положительное целое число.';
      }
      if (numDocumentCount > 30) {
        newErrors.documentCount = 'Слишком много документов для перевода.';
      }
    }

    // Проверка доставки, если опция включена
    if (includeDelivery && !deliveryType) {
      newErrors.deliveryType = 'Выберите тип доставки.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета визового сбора
  const calculateVisaFee = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
  
    setIsCalculating(true);
    setResults(null);
    setFeeBreakdown([]);
  
    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          country, visaType, visaCategory, entryType, processingTime, visaDuration,
          applicantType, applicantsCount, includeServiceFee, includeVAC, includeInsurance,
          includeTranslation, includeDelivery, insuredDays, insuranceCoverage,
          documentCount, deliveryType, paymentCurrency
        } = formData;
  
        // Находим выбранную страну и тип визы
        const selectedCountry = countries.find(c => c.id === country) || {};
        const selectedVisaType = visaTypes.find(t => t.id === visaType) || {};
        
        // Находим выбранное время обработки
        const selectedProcessingTime = processingTimes.find(p => p.id === processingTime) || processingTimes[0];
        
        // Определяем сервисный сбор ВЦ для выбранной страны
        const vacFee = vacServiceFees[country] || vacServiceFees.default;
  
        // Определяем базовый консульский сбор (в EUR/USD в зависимости от страны)
        let baseFee = selectedCountry.defaultFee || 80;
        
        // Корректируем базовый сбор в зависимости от типа визы
        if (selectedVisaType.id === 'business' && !['schengen', 'uk', 'usa', 'canada'].includes(country)) {
          baseFee *= 1.2; // Деловые визы дороже для большинства стран
        } else if (selectedVisaType.id === 'work') {
          baseFee *= 1.5; // Рабочие визы дороже
        } else if (selectedVisaType.id === 'diplomatic' || selectedVisaType.id === 'official') {
          baseFee = 0; // Дипломатические и служебные визы часто бесплатны
        }
  
        // Корректировка по категории визы
        if (visaCategory === 'premium' && ['usa', 'uk', 'canada', 'australia'].includes(country)) {
          baseFee *= 1.3; // Премиум категория для определенных стран
        }
  
        // Корректировка по типу въезда
        if (entryType === 'multiple' && !['usa', 'canada'].includes(country)) {
          baseFee *= 1.4; // Мультивизы дороже
        } else if (entryType === 'double' && !['usa', 'canada'].includes(country)) {
          baseFee *= 1.25; // Двукратные визы дороже
        }
  
        // Корректировка по времени обработки
        baseFee *= selectedProcessingTime.multiplier;
  
        // Базовый консульский сбор за одну визу
        const consulateFee = Math.round(baseFee * 100) / 100;
        
        // Сервисный сбор визового центра (если применяется)
        const serviceFee = includeVAC ? vacFee : 0;
        
        // Расчет страховки (если включена)
        let insuranceCost = 0;
        if (includeInsurance) {
          const numInsuredDays = parseIntOrDefault(insuredDays, 30);
          const coverageMultiplier = insuranceCoverage === 'premium' ? 1.5 : 
                                    insuranceCoverage === 'extended' ? 1.2 : 1.0;
          
          // Базовая ставка страховки ~1 EUR/день, минимум 15 EUR
          const baseInsuranceRate = Math.max(15, numInsuredDays * 1);
          insuranceCost = Math.round(baseInsuranceRate * coverageMultiplier * exchangeRates.EUR);
        }
        
        // Расчет стоимости перевода документов (если включен)
        let translationCost = 0;
        if (includeTranslation) {
          const numDocuments = parseIntOrDefault(documentCount, 5);
          // Примерно 500-800 руб за страницу + заверение
          translationCost = numDocuments * 650;
        }
        
        // Расчет стоимости доставки (если включена)
        let deliveryCost = 0;
        if (includeDelivery) {
          switch (deliveryType) {
            case 'express':
              deliveryCost = 850;
              break;
            case 'courier':
              deliveryCost = 1500;
              break;
            case 'international':
              deliveryCost = 3500;
              break;
            default: // standard
              deliveryCost = 450;
          }
        }
        
        // Количество заявителей
        const numApplicants = parseIntOrDefault(applicantsCount, 1);
        
        // Итоговые суммы с учетом количества заявителей
        const totalConsulateFee = consulateFee * numApplicants;
        const totalServiceFee = serviceFee * numApplicants;
        const totalInsuranceCost = insuranceCost * numApplicants;
        
        // Перевод и доставка считаются один раз на всех заявителей
        const totalTranslationCost = translationCost;
        const totalDeliveryCost = deliveryCost;
        
        // Расчет общей стоимости в EUR
        const totalEurCost = totalConsulateFee + totalServiceFee;
        
        // Расчет общей стоимости в рублях (страховка + перевод + доставка)
        const totalRubAdditionalCosts = totalInsuranceCost + totalTranslationCost + totalDeliveryCost;
        
        // Конвертация в рубли для отображения
        const totalRubCost = Math.round(totalEurCost * exchangeRates.EUR) + totalRubAdditionalCosts;
        
        // Конвертация в выбранную валюту для отображения
        let totalInSelectedCurrency = totalRubCost;
        let selectedCurrencySymbol = '₽';
        
        if (paymentCurrency !== 'rub') {
          switch (paymentCurrency) {
            case 'usd':
              totalInSelectedCurrency = Math.round((totalRubCost / exchangeRates.USD) * 100) / 100;
              selectedCurrencySymbol = '$';
              break;
            case 'eur':
              totalInSelectedCurrency = Math.round((totalRubCost / exchangeRates.EUR) * 100) / 100;
              selectedCurrencySymbol = '€';
              break;
            default:
              // Остаемся в рублях по умолчанию
          }
        }
        
        // Подготовка результата
        const calculationResults = {
          consulateFee,
          serviceFee,
          insuranceCost,
          translationCost,
          deliveryCost,
          
          totalConsulateFee,
          totalServiceFee,
          totalInsuranceCost,
          totalTranslationCost,
          totalDeliveryCost,
          
          totalEurCost,
          totalRubAdditionalCosts,
          totalRubCost,
          
          numApplicants,
          preferredCurrency: paymentCurrency,
          preferredCurrencySymbol: selectedCurrencySymbol,
          totalInPreferredCurrency: totalInSelectedCurrency,
          
          visaCategory,
          entryType,
          processingTime: selectedProcessingTime.id,
          processingTimeMultiplier: selectedProcessingTime.multiplier,
          
          exchangeRates: { ...exchangeRates }
        };
        
        // Генерация детальной разбивки сборов
        const breakdown = generateFeeBreakdown(calculationResults);
        
        setResults(calculationResults);
        setFeeBreakdown(breakdown);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setFeeBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setFeeBreakdown, setIsCalculating, countries, visaTypes, processingTimes, vacServiceFees, exchangeRates, generateFeeBreakdown]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      country: 'schengen',
      visaType: 'tourist',
      visaCategory: 'standard',
      entryType: 'single',
      processingTime: 'standard',
      visaDuration: '90',
      applicantType: 'adult',
      applicantsCount: '1',
      includeServiceFee: false,
      includeVAC: true,
      includeInsurance: false,
      includeTranslation: false,
      includeDelivery: false,
      insuredDays: '30',
      insuranceCoverage: 'standard',
      documentCount: '5',
      deliveryType: 'standard',
      paymentCurrency: 'rub'
    });
    setResults(null);
    setFeeBreakdown([]);
    setErrors({});
    setSaveName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в PDF или CSV
  const exportResults = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // В этом примере генерируем CSV
    const headers = ['Наименование', 'Сумма', 'Валюта', 'Описание'];
    
    const rows = feeBreakdown.map(item => {
      return [
        item.name,
        item.amount.toString().replace('.', ','),
        item.currency,
        item.description
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      'ИТОГО',
      results.totalRubCost.toString().replace('.', ','),
      'RUB',
      `Полная стоимость оформления визы для ${results.numApplicants} чел.`
    ].join(';');
    
    // Данные о выбранной визе
    const selectedCountry = countries.find(c => c.id === formData.country) || {};
    const selectedVisaType = visaTypes.find(t => t.id === formData.visaType) || {};
    
    const infoRows = [
      ['Страна:', selectedCountry.name || formData.country],
      ['Тип визы:', selectedVisaType.name || formData.visaType],
      ['Тип въезда:', formData.entryType === 'single' ? 'Однократная' : formData.entryType === 'double' ? 'Двукратная' : 'Многократная'],
      ['Срок действия:', `${formData.visaDuration} дней`],
      ['Заявителей:', results.numApplicants.toString()],
      ['Время обработки:', processingTimes.find(p => p.id === formData.processingTime)?.name || 'Стандартное'],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_визового_сбора.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateVisaFee();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Globe className="w-6 h-6 text-purple-500" />
          Параметры расчета визового сбора
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Секция выбора визы */}
        <VisaSelectionSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          countries={countries}
          countryCategories={countryCategories}
          visaTypes={visaTypes}
          processingTimes={processingTimes}
        />

        {/* Секция данных о заявителях */}
        <ApplicantDetailsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Секция дополнительных услуг */}
        <AdditionalServicesSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Секция страховки (если опция включена) */}
        {formData.includeInsurance && (
          <InsuranceSection
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {/* Секция документов (если опция включена) */}
        {formData.includeTranslation && (
          <DocumentSection
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет...' : 'Рассчитать визовый сбор'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportResults}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;